/**
 * B31a — the §9.2 postMessage protocol between a tenant's page (the
 * host) and the embedded storefront (the frame). One versioned envelope
 * per direction. Parsing is fail-closed and never throws: window message
 * traffic is public input — anything not exactly ours parses to
 * undefined, and theme hints are sanitized at the parse wall so the
 * frame side is safe by construction.
 *
 * Both sides of the bridge (the loader here, the storefront's bridge
 * module) build messages through the makers below, so the wire shape has
 * exactly one definition.
 */

export const EMBED_PROTOCOL = "transact-embed";
export const EMBED_VERSION = 1;

export interface EmbedPreselect {
  readonly productId?: string;
  readonly slotId?: string;
}

/** Sanitized theme hints: kebab-case keys, short printable values. */
export type ThemeHints = Readonly<Record<string, string>>;

interface Envelope {
  readonly t: typeof EMBED_PROTOCOL;
  readonly v: typeof EMBED_VERSION;
}

/** Host → frame: the handshake payload (site key, preselect, theme). */
export interface InitMessage extends Envelope {
  readonly type: "init";
  readonly siteKey: string;
  readonly preselect?: EmbedPreselect;
  readonly theme?: ThemeHints;
}

export type HostMessage = InitMessage;

/** Frame → host. */
export interface ReadyMessage extends Envelope {
  readonly type: "ready";
}
export interface ResizeMessage extends Envelope {
  readonly type: "resize";
  readonly height: number;
}
export interface SuccessMessage extends Envelope {
  readonly type: "success";
  readonly orderId: string;
}
export interface CancelMessage extends Envelope {
  readonly type: "cancel";
}

/** B517 — the inline list asks the host to open the booking dialog for one product. */
export interface OpenMessage extends Envelope {
  readonly type: "open";
  readonly productId: string;
}
/** B517 — the dialog's own close door (✕ / Esc inside the frame). */
export interface CloseMessage extends Envelope {
  readonly type: "close";
}

/**
 * B524 — one GA4 ecommerce event for the HOST page's `dataLayer`. The
 * storefront validates it against `@transact/contracts`' `zGtmEvent`
 * (closed set, strict, no PII) before posting; this side re-checks only
 * what the host needs to trust it as data: a snake_case event name and
 * object payloads, and only those three keys cross (postMessage data is
 * already a structured clone, so the host never shares a reference).
 */
export interface TrackMessage extends Envelope {
  readonly type: "track";
  readonly event: TrackEvent;
}
export interface TrackEvent {
  readonly event: string;
  readonly ecommerce: Readonly<Record<string, unknown>>;
  readonly transact: Readonly<Record<string, unknown>>;
}

/**
 * The loader accepts any snake_case event NAME rather than carrying GA4's
 * list: the list cost the paste-once snippet more bytes than the whole
 * relay (measured 2,882 → 3,129 B gzipped, past the 3 KB wall), and the
 * name set is already closed where it is authored — the storefront parses
 * every event with `zGtmEvent` before posting, and only the storefront's
 * own frame (origin AND source) is heard here at all.
 */
const TRACK_NAME = /^[a-z][a-z_]{2,31}$/;

export type FrameMessage =
  | ReadyMessage
  | ResizeMessage
  | SuccessMessage
  | CancelMessage
  | OpenMessage
  | CloseMessage
  | TrackMessage;

const MAX_ID = 256;
const MAX_HEIGHT = 32767;
const MAX_THEME_ENTRIES = 16;
const THEME_KEY = /^[a-z][a-z0-9-]{0,31}$/;
const THEME_VALUE = /^[\x20-\x7e]{1,64}$/;

function envelope(): Envelope {
  return { t: EMBED_PROTOCOL, v: EMBED_VERSION };
}

export function initMessage(siteKey: string, preselect?: EmbedPreselect, theme?: ThemeHints): InitMessage {
  return {
    ...envelope(),
    type: "init",
    siteKey,
    ...(preselect !== undefined ? { preselect } : {}),
    ...(theme !== undefined ? { theme } : {}),
  };
}

export function readyMessage(): ReadyMessage {
  return { ...envelope(), type: "ready" };
}

export function resizeMessage(height: number): ResizeMessage {
  return { ...envelope(), type: "resize", height };
}

export function successMessage(orderId: string): SuccessMessage {
  return { ...envelope(), type: "success", orderId };
}

export function cancelMessage(): CancelMessage {
  return { ...envelope(), type: "cancel" };
}

export function openMessage(productId: string): OpenMessage {
  return { ...envelope(), type: "open", productId };
}

export function closeMessage(): CloseMessage {
  return { ...envelope(), type: "close" };
}

export function trackMessage(event: TrackEvent): TrackMessage {
  return { ...envelope(), type: "track", event };
}

type Raw = Record<string, unknown>;

function plain(value: unknown): value is Raw {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function sanitizeTrack(value: unknown): TrackEvent | undefined {
  if (!plain(value) || typeof value["event"] !== "string" || !TRACK_NAME.test(value["event"])) return undefined;
  const { ecommerce, transact } = value;
  /* Only the three GA4 keys cross; the data is already a structured clone. */
  return plain(ecommerce) && plain(transact) ? { event: value["event"], ecommerce, transact } : undefined;
}

function raw(data: unknown): Raw | undefined {
  if (typeof data !== "object" || data === null || Array.isArray(data)) return undefined;
  const r = data as Raw;
  if (r["t"] !== EMBED_PROTOCOL || r["v"] !== EMBED_VERSION) return undefined;
  return r;
}

function id(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 && value.length <= MAX_ID ? value : undefined;
}

function sanitizePreselect(value: unknown): EmbedPreselect | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const r = value as Raw;
  const productId = id(r["productId"]);
  const slotId = id(r["slotId"]);
  if (productId === undefined && slotId === undefined) return undefined;
  return {
    ...(productId !== undefined ? { productId } : {}),
    ...(slotId !== undefined ? { slotId } : {}),
  };
}

function sanitizeTheme(value: unknown): ThemeHints | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const out: Record<string, string> = {};
  let kept = 0;
  for (const key of Object.keys(value as Raw).sort()) {
    if (kept >= MAX_THEME_ENTRIES) break;
    const v = (value as Raw)[key];
    if (!THEME_KEY.test(key) || typeof v !== "string" || !THEME_VALUE.test(v)) continue;
    out[key] = v;
    kept += 1;
  }
  return kept > 0 ? out : undefined;
}

/** Frame side: parse a message claiming to come from the host page. */
export function parseHostMessage(data: unknown): HostMessage | undefined {
  const r = raw(data);
  if (r === undefined || r["type"] !== "init") return undefined;
  const siteKey = id(r["siteKey"]);
  if (siteKey === undefined) return undefined;
  const preselect = sanitizePreselect(r["preselect"]);
  const theme = sanitizeTheme(r["theme"]);
  return initMessage(siteKey, preselect, theme);
}

/** Host side: parse a message claiming to come from the frame. */
export function parseFrameMessage(data: unknown): FrameMessage | undefined {
  const r = raw(data);
  if (r === undefined) return undefined;
  switch (r["type"]) {
    case "ready":
      return readyMessage();
    case "cancel":
      return cancelMessage();
    case "resize": {
      const height = r["height"];
      if (typeof height !== "number" || !Number.isFinite(height)) return undefined;
      if (height < 0 || height > MAX_HEIGHT) return undefined;
      return resizeMessage(Math.round(height));
    }
    case "success": {
      const orderId = id(r["orderId"]);
      return orderId === undefined ? undefined : successMessage(orderId);
    }
    case "open": {
      const productId = id(r["productId"]);
      return productId === undefined ? undefined : openMessage(productId);
    }
    case "close":
      return closeMessage();
    case "track": {
      const event = sanitizeTrack(r["event"]);
      return event === undefined ? undefined : trackMessage(event);
    }
    default:
      return undefined;
  }
}
